import { useMemo, useState } from 'react';
import { formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { TrafficResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatCard } from '../../ui/components';

export function Traffic() {
  const { persona } = useAuth();
  const isDict = persona?.roles.includes('SEYX_ADMIN') ?? false;

  const [consumer, setConsumer] = useState('');
  const [service, setService] = useState('');

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (consumer) p.set('consumer', consumer.toUpperCase());
    if (service) p.set('service', service.toLowerCase());
    const qs = p.toString();
    return `/api/v1/seyx/traffic${qs ? `?${qs}` : ''}`;
  }, [consumer, service]);
  const trafficQ = useApiQuery<TrafficResponse>(isDict ? query : null);

  if (!isDict) {
    return (
      <div>
        <PageHeader title="Traffic" subtitle="Per-member / per-service call volume, error rate &amp; latency" />
        <EmptyState title="No access" hint="The traffic dashboard is DICT-only (SEYX_ADMIN) — GET /traffic gates on that role alone." />
      </div>
    );
  }

  const totals = trafficQ.data?.totals;
  const errorRate = totals && totals.calls > 0 ? ((totals.errors / totals.calls) * 100).toFixed(1) : null;

  return (
    <div>
      <PageHeader title="Traffic" subtitle="Per-member / per-service call volume, error rate & latency — aggregated daily by the exchange gateway" />

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          className="field-input max-w-[160px] font-mono"
          placeholder="Consumer MDA"
          value={consumer}
          onChange={(e) => setConsumer(e.target.value)}
        />
        <input
          className="field-input max-w-[220px] font-mono"
          placeholder="Service code"
          value={service}
          onChange={(e) => setService(e.target.value)}
        />
      </div>

      {trafficQ.error ? (
        <div className="mb-4">
          <ErrorNote message={trafficQ.error} />
        </div>
      ) : null}

      {trafficQ.loading ? (
        <Spinner />
      ) : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Total calls" value={(totals?.calls ?? 0).toLocaleString()} />
            <StatCard label="Errors" value={(totals?.errors ?? 0).toLocaleString()} tone={(totals?.errors ?? 0) > 0 ? 'bad' : 'good'} />
            <StatCard label="Error rate" value={errorRate !== null ? `${errorRate}%` : '—'} tone={errorRate && Number(errorRate) > 5 ? 'warn' : 'default'} />
            <StatCard label="Avg latency" value={`${totals?.avgMs ?? 0} ms`} />
          </div>

          <div className="card overflow-x-auto">
            {(trafficQ.data?.rows.length ?? 0) === 0 ? (
              <EmptyState title="No traffic recorded" hint="The exchange gateway reports daily aggregates via POST /traffic/report." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Day</th>
                    <th className="table-head">Consumer</th>
                    <th className="table-head">Provider</th>
                    <th className="table-head">Service</th>
                    <th className="table-head text-right">Calls</th>
                    <th className="table-head text-right">Errors</th>
                    <th className="table-head text-right">Avg ms</th>
                  </tr>
                </thead>
                <tbody>
                  {trafficQ.data!.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="table-cell text-slate-500">{formatDate(r.day)}</td>
                      <td className="table-cell font-mono text-xs">{r.consumerMember}</td>
                      <td className="table-cell font-mono text-xs">{r.providerMember}</td>
                      <td className="table-cell font-mono text-xs">{r.serviceCode}</td>
                      <td className="table-cell text-right tabular-nums">{r.calls.toLocaleString()}</td>
                      <td className={`table-cell text-right tabular-nums ${r.errors > 0 ? 'text-rose-600' : ''}`}>{r.errors.toLocaleString()}</td>
                      <td className="table-cell text-right tabular-nums">{r.calls > 0 ? Math.round(r.totalMs / r.calls) : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
